import { app } from "@/main";
import type { AuthService } from "@/services/authService";
import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import * as constants from "@/constants";
import AppOidcCallback from "@/components/AppOidcCallback.vue";
import NotificationUtil from "@/utils/NotificationUtil";
import { useProfileStore } from "@/stores/profile";
import { ERROR_PAGE_URI } from "@/constants";
import { useGlobalErrorStore } from "@/stores/global.error";

const routes: RouteRecordRaw[] = [
  {
    path: constants.OIDC_CALLBACK_URI,
    name: "oidcCallback",
    component: AppOidcCallback,
  },
  {
    path: "/",
    name: "index",
    redirect: "/finanslovsstruktur",
  },
  {
    path: "/regnskabsstruktur",
    name: constants.ROUTE.Regnskabsstruktur,
    component: () => import("@/views/RegnskabsstrukturView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/kontoplan",
    name: constants.ROUTE.Kontoplan,
    component: () => import("@/views/KontoplanView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/finanslovsstruktur",
    name: constants.ROUTE.Finanslovsstruktur,
    component: () => import("@/views/FinanslovsstrukturView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/create-element-overview",
    name: constants.ROUTE.CreateElementOverviewView,
    component: () => import("@/views/CreateElementOverviewView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/create-element-overview/:commandType",
    name: constants.ROUTE.CreatElementView,
    component: () => import("@/views/CreatElementView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/processoverview",
    name: constants.ROUTE.ProcessOverview,
    component: () => import("@/views/ProcessOverview.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/process/:processid",
    name: constants.ROUTE.EditProcess,
    component: () => import("@/views/EditProcessView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/process",
    name: constants.ROUTE.NewProcess,
    component: () => import("@/views/EditProcessView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/process/:processid/main-activity",
    name: constants.ROUTE.NewMainActivity,
    component: () => import("@/views/EditMainActivityView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/process/main-activity/:activityid",
    name: constants.ROUTE.EditMainActivity,
    component: () => import("@/views/EditMainActivityView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/main-activity/:mainactivityid/sub-activity",
    name: constants.ROUTE.NewSubActivity,
    component: () => import("@/views/EditSubActivityView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/main-activity/sub-activity/:subactivityid",
    name: constants.ROUTE.EditSubActivity,
    component: () => import("@/views/EditSubActivityView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/logout",
    name: "logout",
    component: () => import("@/views/LogoutView.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/fl/:number",
    name: constants.ROUTE.FinanslovsstrukturViewDetail,
    component: () => import("@/views/FLElementDetailView.vue"),
    props: (route) => ({ underaktivitetId: route.query.underaktivitetId }),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/ac/:number",
    name: constants.ROUTE.RegnskabsstrukturViewDetail,
    component: () => import("@/views/ACElementDetailView.vue"),
    props: (route) => ({ period: route.query.period }),
    meta: {
      requiresAuth: true,
      mode: "ac",
    },
  },
  {
    path: "/kt/:number",
    name: constants.ROUTE.KontoplanViewDetail,
    component: () => import("@/views/ACElementDetailView.vue"),
    props: (route) => ({ period: route.query.period }),
    meta: {
      requiresAuth: true,
      mode: "kt",
    },
  },
  {
    path: "/perioder",
    name: constants.ROUTE.PeriodsView,
    component: () => import("@/views/PeriodsView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/aktiviteter",
    name: constants.ROUTE.AktiviteterView,
    component: () => import("@/views/AktiviteterView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/loengrupper",
    name: constants.ROUTE.LoengrupperView,
    component: () => import("@/views/LoengrupperView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/loenfaellesskab",
    name: constants.ROUTE.CreateLoenfaellesskabView,
    component: () => import("@/views/CreateLoenfaellesskabView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/loenfaellesskab/:nummer",
    name: constants.ROUTE.LoenfaellesskabDetail,
    component: () => import("@/views/LoenfaellesskabDetailView.vue"),
    props: (route) => ({ nummer: Number(route.params.nummer) }),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/loengruppe",
    name: constants.ROUTE.CreateLoengruppeView,
    component: () => import("@/views/CreateLoengruppeView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/loengruppe/:nummer",
    name: constants.ROUTE.LoengruppeDetail,
    component: () => import("@/views/LoengruppeDetailView.vue"),
    props: (route) => ({ nummer: Number(route.params.nummer) }),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/kontobroer",
    name: constants.ROUTE.Kontobroer,
    component: () => import("@/views/KontobroerView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/rapport/reg",
    name: constants.ROUTE.RapportRegView,
    component: () => import("@/views/RapportRegView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/rapport/reg-bogfoeringskredse-delregnskaber-underkonti",
    name: constants.ROUTE.RapportRegBogforingskredseDelregnskaberOgUnderkonti,
    component: () => import("@/views/RapportRegBogforingskredseDelregnskaberOgUnderkonti.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/rapport/fl",
    name: constants.ROUTE.RapportFLView,
    component: () => import("@/views/RapportFLView.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: ERROR_PAGE_URI,
    name: "error",
    component: () => import("@/views/Error.vue"),
    meta: {
      requiresAuth: false,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, _from, next) => {
  const globalError = useGlobalErrorStore();
  const authService = app.config.globalProperties.$authService as AuthService;
  // Global guarding for route that requires authentication
  let nextIsCalled = false;
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const isLoggedIn = await authService.isLoggedIn();
    if (!isLoggedIn) {
      const path = to.fullPath ?? "index";
      await authService.signIn(path).catch((err: any) => {
        globalError.redirect(constants.ERROR_CODE.AUTHENTICATION_SERVER, router);
      });
    } else {
      const userProfile = useProfileStore();

      if (to.name === constants.ROUTE.CreatElementView) {
        await userProfile.setAccessControl();
        const commandTypeAsString = to.params.commandType.toString();
        const isContainRole =
          userProfile.accessibleCommand?.isContainRoleString(commandTypeAsString);
        // Check if the user has access to the route
        if (!isContainRole) {
          //Show toast
          NotificationUtil.notify403("Adgang nægtet");
          // Redirect to createElementOverviewView
          next({
            name: constants.ROUTE.CreateElementOverviewView,
          });
          nextIsCalled = true;
        }
      }

      if (to.name === constants.ROUTE.AktiviteterView) {
        await userProfile.setAccessControl();
        // Check if the user has access to the route
        if (!userProfile.accessibleCommand?.hasAccessActivitiesTab) {
          //Show toast
          NotificationUtil.notify403("Adgang nægtet");
          // Redirect to Finanslovsstruktur
          next({
            name: "index",
          });
          nextIsCalled = true;
        }
      }

      if (
        to.name === constants.ROUTE.ProcessOverview ||
        to.name === constants.ROUTE.NewProcess ||
        to.name === constants.ROUTE.EditProcess ||
        to.name === constants.ROUTE.NewMainActivity ||
        to.name === constants.ROUTE.EditMainActivity ||
        to.name === constants.ROUTE.NewSubActivity ||
        to.name === constants.ROUTE.EditSubActivity
      ) {
        await userProfile.setAccessControl();
        // Check if the user has access to the route
        if (!userProfile.accessibleCommand?.hasAccessToProcess) {
          //Show toast
          NotificationUtil.notify403("Adgang nægtet");
          // Redirect to search Finanslovsstruktur
          next({
            name: constants.ROUTE.Finanslovsstruktur,
          });
          nextIsCalled = true;
        }
      }

      if (
        to.name === constants.ROUTE.LoengrupperView ||
        to.name === constants.ROUTE.LoenfaellesskabDetail
      ) {
        await userProfile.setAccessControl();
        if (!userProfile.accessibleCommand?.hasAccessLoengrupperTab) {
          NotificationUtil.notify403("Adgang nægtet");
          next({
            name: "index",
          });
          nextIsCalled = true;
        }
      }

      if (
        to.name === constants.ROUTE.CreateLoenfaellesskabView ||
        to.name === constants.ROUTE.CreateLoengruppeView
      ) {
        await userProfile.setAccessControl();
        if (!userProfile.accessibleCommand?.hasAccessToEditLoengrupperOrFaellesskaber) {
          NotificationUtil.notify403("Adgang nægtet");
          next({
            name: "index",
          });
          nextIsCalled = true;
        }
      }

      if (to.name === constants.ROUTE.Kontobroer) {
        await userProfile.setAccessControl();
        if (!userProfile.accessibleCommand?.hasAccessKontobroerTab) {
          NotificationUtil.notify403("Adgang nægtet");
          next({
            name: "index",
          });
          nextIsCalled = true;
        }
      }
    }
  }

  // Allow navigation to continue
  if (!nextIsCalled) {
    next();
  }
});

export default router;
